'use client';

import { useState } from 'react';
import { PhoneIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { clsx } from 'clsx';
import { Button } from '@components/common';
import { useSwrRecruiterCallQuote } from '@hooks/useSwrApi/endpoints/recruiter-call';
import { RecruiterCallSoftphoneModal } from './RecruiterCallSoftphoneModal';

interface CallCandidateActionProps {
  candidateName: string;
  candidateUserId: number;
  jobSlug?: string | null;
  /** Extra classes for the outer relative wrapper (spacing/positioning next to sibling buttons). */
  className?: string;
}

/**
 * Toolbar variant of the RecruiterCall entry point. Sits between "Contactează"
 * and "Shortlist" in the applicant actions bar and follows the same visual
 * pattern:
 *   - green pill badge floating above the button with the price,
 *   - button hidden entirely when the backend's canCall check returns false.
 *
 * The click always opens the softphone modal (which owns the confirm
 * step + the WebRTC softphone). Zero-price still opens the modal — it
 * just labels the call as free instead of showing a credit count.
 */
export const CallCandidateAction = ({
  candidateName,
  candidateUserId,
  jobSlug,
  className,
}: CallCandidateActionProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useSwrRecruiterCallQuote({ candidateUserId, jobSlug });

  if (isLoading || !data?.canCall) {
    return null;
  }
  const priceCredits = data.priceCredits ?? 0;
  const isFree = priceCredits === 0;
  const labelKey = isFree ? 'cv.detail.action.free-call.label' : 'cv.detail.action.call.label';
  const label = t(labelKey, { defaultValue: isFree ? 'Sună gratuit' : 'Sună' });

  return (
    <>
      <div className={clsx('relative inline-block', className)}>
        {!isFree && (
          <div className="absolute -top-3.5 left-1/2 z-10 min-w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-secondary px-1 text-pico font-semibold text-surface">
            {`${t('cv.detail.action.call.price_label')}: `}
            <span className="ml-1">
              {priceCredits} {t('global.label.credits')}
            </span>
          </div>
        )}
        <Button
          type="button"
          color="secondary"
          onClick={() => setOpen(true)}
          className="relative mx-1 flex h-12 min-w-32 items-center justify-center rounded-2xl !text-md text-white hover:!bg-secondary/90"
          title={label}
        >
          <PhoneIcon className="mr-2 size-4" />
          {label}
        </Button>
      </div>

      {open && (
        <RecruiterCallSoftphoneModal
          open={open}
          candidateName={candidateName}
          candidateUserId={candidateUserId}
          jobSlug={jobSlug ?? null}
          priceCredits={priceCredits}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};
